import type { FC, JSX } from "react";

import styled, { css } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFont } from "@similarweb/styles";
import { Box, ItemIcon, Link, Switcher, Tab, TabList, TabPanel } from "@similarweb/ui-components";

import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import { SWReactTableNoDataWrapper } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { StyledPrimaryTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { CenteredFlexRow, FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const getMaxMediaScreenSize = (legendsAmount) => {
    return legendsAmount < 4 ? "1300px" : "1600px";
};
export const ChartSection = styled.div`
    padding: 0 24px 10px 24px;
`;

export const ChartContainer = styled.div`
    width: calc(100% - 248px);
    margin-right: 24px;
`;
export const CheckboxesContainer = styled(FlexColumn)`
    width: 248px;
    padding-top: 4px;
    gap: 8px 0;
`;

export const ButtonsContainer = styled.div<{ legendsAmount: number }>`
    margin-left: auto;
    grid-column: 2;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    @media print {
        display: none;
    }
    ${({ legendsAmount }) => `
        @media (max-width: ${getMaxMediaScreenSize(legendsAmount)}) {
        grid-column: 1;
        }
    `}
`;

export const UtilitiesContainer = styled.div<{ legendsAmount: number }>`
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    grid-template-columns: auto auto;
    ${({ legendsAmount }) => `
        @media (max-width: ${getMaxMediaScreenSize(legendsAmount)}) {
            grid-template-columns: 100%;
            grid-row: 2;
        }
        min-height: ${legendsAmount > 1 ? "54px" : ""};
    `}
`;

export const FlexWrapper = styled.div`
    display: flex;
`;

export const StyledSWReactIcons = styled(SWReactIcons)<{ color?: string }>`
    ${({ color }) =>
        color &&
        `
        svg {
            path {
                fill: ${color}
            }
        }
    `}
`;

export const GraphTypeSwitcherContainer = styled(Switcher)`
    border-right: 1px solid ${colorsPalettes.carbon[50]};
    margin-right: 20px;
    height: fit-content;
`;

export const Bullet = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${({ background }) => background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
`;

export const Text = styled.label<{
    color?: string;
    size?: number;
    opacity?: number;
    rightMargin?: number;
}>`
    cursor: inherit;
    margin-right: 20px;
    ${({ rightMargin = 20 }) => `margin-right:${rightMargin}px`};
    ${({ size = 14 }) => setFont({ $size: size })};
    ${({ color }) => `color:${color};`};
`;

export const TableWrapper: any = styled.div`
    pointer-events: ${({ loading }: any) => (loading ? "none" : "all")};
`;

export const BoldText = styled(Text)`
    font-weight: 500;
`;

export const GridBox = styled.div`
    width: 290px;
    display: grid;
    grid-template-columns: 30% 23% 23% 23%;
    row-gap: 10px;
`;
export const Line = styled.hr`
    grid-column: 1 / -1;
    margin: 0;
`;

export const TabWrapperStyle = styled(TabList)`
    margin-bottom: 16px;
`;
export const BadgeContainer = styled.div<{ disabled?: boolean }>`
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.4;
        `}
`;
export const CompareTab = styled(Tab)`
    font-size: 13px;
    height: 48px;
    .SWReactIcons svg path {
        fill: ${colorsPalettes.carbon[200]};
    }
    &.selected {
        .SWReactIcons svg path {
            fill: ${colorsPalettes.blue[400]};
        }
    }
    &:hover {
        .SWReactIcons svg path {
            fill: ${colorsPalettes.carbon[500]};
        }
    }
    &[aria-disabled="true"] {
        cursor: default;
    }
`;
export const Icon = styled(SWReactIcons)`
    margin-right: 15px;
`;

export const TrafficAndEngagementContainer = styled.div`
    background-color: white;
    height: fit-content;
    min-height: 500px;
`;

export const TabPanelStyled = styled(TabPanel)`
    padding-top: 10px;
`;

export const PngHeaderContentContainer = styled.div`
    align-items: baseline;
    display: flex;
    padding-bottom: 30px;
    border-bottom: 1px solid ${colorsPalettes.carbon[100]};
`;

export const PngHeaderContainer = styled.div`
    display: none;
    @media print {
        display: block;
    }
`;

export const BoxContainer = styled(Box)`
    height: 164px;
    margin-bottom: 24px;
    flex: 1 0 350px;
    max-width: 45%;
    padding: 14px 25px 25px;
    display: flex;
    flex-direction: column;
`;

export const StyledItemIcon = styled(ItemIcon)`
    width: 24px;
    height: 24px;
`;

export const HeaderWrapper = styled(FlexRow)<{ bottomMargin?: string; topMargin?: string }>`
    align-items: center;
    justify-content: space-between;
    margin-top: ${({ topMargin }) => (topMargin ? topMargin : "")};
    margin-bottom: ${({ bottomMargin }) => (bottomMargin ? bottomMargin : "")};
`;

export const LabelStyled = styled.div`
    font-size: 16px;
`;
export const DataRow = styled(FlexRow)`
    padding: 8px 0 8px 0;
    justify-content: space-between;
    height: 40px;
`;
export const TopPageWidgetsRow = styled(FlexRow)`
    width: 100%;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;

    &:has(> :nth-child(4)) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-auto-rows: 1fr;
    }

    & > * {
        min-width: 0;
        height: 100%;
    }
`;
export const NumberStyled = styled.div`
    padding-right: 4px;
    font-size: 26px;
    font-weight: 300;
`;
export const Separator = styled.div`
    width: 100%;
    height: 1px;
    background-color: ${colorsPalettes.carbon["100"]};
`;

export const ChangeText = styled.div`
    font-size: 12px;
    color: ${colorsPalettes.carbon["400"]};
`;

export const SlantSeparator = styled.div`
    height: 100%;
    &:before {
        content: "";
        display: block;
        height: 100%;
        width: 1px;
        background-color: ${colorsPalettes.carbon["50"]};
        transform: rotate(12deg);
    }
`;

export const TotalAndTrafficWrapper = styled(FlexRow)`
    margin-top: 24px;
    height: 52px;
    justify-content: space-between;
    max-width: 325px;
    align-items: center;
`;

export const TotalNumberStyled = styled.div`
    font-size: 45px;
    color: ${colorsPalettes.midnight["400"]};
    font-weight: 300;
`;

export const ChangeWrapper = styled(FlexRow)`
    margin-top: 8px;
    gap: 8px;
    align-items: center;
`;

export const GAContainer = styled.div<{ rightMargin?: number; leftMargin?: number }>`
    margin-left: ${({ leftMargin = 5 }) => `${leftMargin}px`};
    margin-right: ${({ rightMargin }) => (rightMargin ? `${rightMargin}px` : "")};
`;

export const TrafficShareStyled = styled.div`
    font-size: 25px;
    font-weight: 300;
`;

export const StyledTitle = styled(StyledPrimaryTitle)`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
`;

export const RankDataRow = styled(FlexRow)`
    justify-content: space-between;
    padding: 8px 0px 8px 16px;
    height: 40px;
    align-items: center;
`;

export const TextStyled = styled.div`
    font-size: 16px;
    color: ${colorsPalettes.carbon["400"]};
`;

export const CountryIconContainer = styled.div`
    padding-right: 4px;
    height: 12px;
    width: 12px;
`;

export const CountryRowWrapper = styled(FlexRow)`
    align-items: baseline;
`;

export const RankNumberStyled = styled.div`
    padding-right: 6px;
    font-weight: 200;
    color: ${colorsPalettes.midnight["400"]};
    font-size: 25px;
`;

export const SmallSeparator = styled.div`
    height: 1px;
    width: 170px;
    font-weight: 200;
    background-color: ${colorsPalettes.carbon["100"]};
`;

export const BetaBranchLabelWrapper = styled.div`
    margin-right: -13px;
`;

export const MarginTopWrapper = styled.div<{ topMargin?: number }>`
    margin-top: ${({ topMargin }) => (topMargin ? `${topMargin}px` : "")};
`;

export const EngagementOverviewTableSeparator = styled.div`
    height: 1px;
    width: 100%;
    background-color: ${colorsPalettes.carbon["100"]};
`;

const StyledLinkWithContent = styled.a`
    color: ${colorsPalettes.blue["500"]};
    text-decoration: none;
    width: 100%;
`;

export const StyledLinkWrapper: FC<{ children?: string | JSX.Element; url?: string }> = ({ children, url }) =>
    url ? <StyledLinkWithContent href={url}>{children}</StyledLinkWithContent> : <>{children}</>;

export const StyledLink = styled(Link)`
    color: ${colorsPalettes.blue["500"]};
    text-decoration: underline;
    width: 234px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
`;

export const StyledBoxSubtitleWrapper: any = styled(StyledBoxSubtitle)`
    margin-bottom: 10px;
`;

export const WidgetContent = styled.div`
    min-height: 100px;
    max-height: 109px;
    padding: 4px 0;
    ${SWReactTableNoDataWrapper} {
        padding: 0;
        img {
            max-height: 51px;
        }
    }
    .swReactTable-container {
        .swReactTableCell {
            &.no-ga-verified-icon {
                .GAVerified-container {
                    display: none;
                }
            }
        }
    }
`;

export const CalculationCellWrapper = styled.div`
    [data-automation="badge"] {
        margin-left: 0.5em;
    }

    .GAVerified-container {
        margin-left: 0.5em;
        vertical-align: bottom;

        .GAVerified-icon-container {
            width: 1.25em;
            height: 1.25em;
            background-size: 0.7em 0.7em;
            background-position: 50% 50%;
        }
    }
`;

export const TopPageWidgetsRowWrap = styled(TopPageWidgetsRow)`
    width: 100%;
`;
export const NoDataWrapper = styled(CenteredFlexRow)`
    flex: 1;
    height: 100%;
`;
